/// <amd-module name="CodeCoverage/CodeCoverageCiFileCoveragePane/CodeEditorGutterAdornments/GutterAdornmentsWidget" />
import { GutterAdornment, OverlayWidget } from "CodeCoverage/CodeCoverageCiFileCoveragePane/CodeEditorGutterAdornments/AdornmentContracts";
import { AdornmentToolTipContainerHost } from "CodeCoverage/CodeCoverageCiFileCoveragePane/CodeEditorGutterAdornments/AdornmentToolTipContainerHost";

export class GutterAdornmentsWidget implements OverlayWidget {
    private _$domNode: HTMLElement;
    private _editorFontInfo: monaco.editor.BareFontInfo;
    private _lineNumberWidth: number;
    private _adornmentCollection: { [id: string]: GutterAdornment };
    private _adornmentElementCollection: { [id: string]: any };

    constructor(private _editor: monaco.editor.ICodeEditor) {
        this._editorFontInfo = this._editor.getOption(monaco.editor.EditorOption.fontInfo);
        this._lineNumberWidth = this._editor.getLayoutInfo().lineNumbersWidth;
        this._adornmentCollection = {};
        this._adornmentElementCollection = {};

        this._subscribeToEditorEvents();
    }

    public getId() {
        return "gutter-adornment-zone-widget-original";
    }

    public getDomNode() {
        if (!this._$domNode) {
            this._$domNode = document.createElement("div");
            this._$domNode.className = "gtr-adrnmnts-ctnr";
        }
        return this._$domNode;
    }

    public getPosition() {
        return null;
    }

    public removeAdornment(adornment: GutterAdornment) {
        if (this._adornmentCollection[adornment.id] === adornment) {
            this._adornmentElementCollection[adornment.id].remove();
            delete this._adornmentCollection[adornment.id];
            delete this._adornmentElementCollection[adornment.id];
        }
    }

    public addAdornment(adornment: GutterAdornment) {
        const startLine = adornment.startLine > 0 ? adornment.startLine : 1;
        const endLine = adornment.endLine;

        if (startLine < endLine) {
            let adornmentHeight = this._editor.getTopForLineNumber(endLine) - this._editor.getTopForLineNumber(startLine);

            if (endLine > this._editor.getModel()!.getLineCount()) {
                adornmentHeight += this._editorFontInfo.lineHeight;
            }

            // Create dom node for adornment
            const topForLineNumber = this._editor.getTopForLineNumber(adornment.startLine);

            const nodeStyle = `position: absolute; top: ${topForLineNumber}px; 
            line-height: ${this._editorFontInfo.lineHeight}px; 
            font-family: ${this._editorFontInfo.fontFamily}; font-size:${this._editorFontInfo.fontSize}px;
            font-weight: ${this._editorFontInfo.fontWeight}; letter-spacing: ${this._editorFontInfo.letterSpacing};
            width: ${this._lineNumberWidth + 4}px; text-align: right; height: ${adornmentHeight.toString()}px `;

            const adornmentNode = document.createElement("div");
            adornmentNode.className = adornment.className;
            adornmentNode.setAttribute("style", nodeStyle);

            // Create line number container
            const lineNumberContainer = document.createElement("div");
            lineNumberContainer.className = "line-number-container";
            if (adornment.showLineNumber) {
                this._appendAdornmentLineNumbers(startLine, endLine, lineNumberContainer);
                adornmentNode.appendChild(lineNumberContainer);
            }

            // If helptext is not empty then show tool tip host
            if (adornment.helpText) {
                const toolTipContainer = AdornmentToolTipContainerHost.createToolTipContainer(adornment.helpText);
                adornmentNode.appendChild(toolTipContainer);
            }

            // Append to main container
            if (this._$domNode) {
                this._$domNode.appendChild(adornmentNode);
            }

            // cache adornment
            this._adornmentCollection[adornment.id] = adornment;

            // remove element if there was a conflict in cache
            if (this._adornmentElementCollection[adornment.id]) {
                this._adornmentElementCollection[adornment.id].remove();
            }

            // cache adornment node
            this._adornmentElementCollection[adornment.id] = adornmentNode;
        }
    }

    private _subscribeToEditorEvents() {
        // onDidChangeViewZones is not a part of the Monaco.Editor.d.ts declaration but is part of the monaco API
        (this._editor as any).onDidChangeViewZones(() => {
            this._updateAdornments();
        });

        this._editor.onDidScrollChange((e: monaco.IScrollEvent) => {
            const scrollTop = e.scrollTop || 0;
            const top = this._editor.getTopForLineNumber(0) - scrollTop;
            this.getDomNode().style.top = top.toString() + "px";
        });

        this._editor.onDidLayoutChange((e: monaco.editor.EditorLayoutInfo) => {
            if (e.lineNumbersWidth !== this._lineNumberWidth) {
                this._lineNumberWidth = e.lineNumbersWidth;
                this._updateAdornmentsLayout();
            }
        });
    }

    /**
     * Gutter width can change when content loads
     * For example before the file has been loaded the default width is 33px
     * If content has more than 99 lines, the gutter width adjusts to account for the 3rd char
     */
    private _updateAdornmentsLayout() {
        for (const key in this._adornmentElementCollection) {
            if (this._adornmentElementCollection.hasOwnProperty(key)) {
                const adornmentNode = this._adornmentElementCollection[key];

                adornmentNode.css({
                    width: this._lineNumberWidth + "px"
                });
            }
        }
    }

    /** Needs to be called whenever there is a change in viewzone */
    private _updateAdornments() {
        for (const key in this._adornmentElementCollection) {
            if (this._adornmentElementCollection.hasOwnProperty(key)) {
                const adornment = this._adornmentCollection[key];
                const adornmentNode = this._adornmentElementCollection[key];

                const startLine = adornment.startLine > 0 ? adornment.startLine : 1;
                const endLine = adornment.endLine;

                const startLineTop = this._editor.getTopForLineNumber(startLine);

                adornmentNode.css({
                    top: startLineTop + "px",
                    height: (this._editor.getTopForLineNumber(endLine - 1) - startLineTop + this._editorFontInfo.lineHeight).toString() + "px"
                });

                const lineNumberContainer = adornmentNode.find(".line-number-container");
                lineNumberContainer.empty();

                if (adornment.showLineNumber === true) {
                    this._appendAdornmentLineNumbers(startLine, endLine, lineNumberContainer);
                }
            }
        }
    }

    private _appendAdornmentLineNumbers(startLine: number, endLine: number, container: HTMLElement) {
        for (let i = startLine, prevTop; i < endLine; i++) {
            const top = this._editor.getTopForLineNumber(i);

            if (prevTop && prevTop + this._editorFontInfo.lineHeight !== top) {
                // append empty space between lines to adjust for view zones
                container.append(document.createElement("div"));
                container.className = "view-zone-block padding-right-4 ";
                container.style.height = (top - prevTop - this._editorFontInfo.lineHeight).toString() + "px";
            }

            const lineElement = document.createElement("div");
            lineElement.style.height = this._editorFontInfo.lineHeight + "px";
            lineElement.innerHTML = i.toString();
            container.appendChild(lineElement);

            prevTop = top;
        }
    }
}
